// pages/dashboard/AdminLayout.jsx
// ─────────────────────────────────────────────────────────────
// FIXES:
//
// 1. BUG — require("./page") was called INSIDE handleNavChange,
//    a function body. This is an ES module file (React/Vite) —
//    require() doesn't exist and throws at runtime, meaning
//    EVERY alert click and nav change silently failed.
//    Fixed: NAV_TO_PATH imported at the top with the other imports.
//
// 2. BUG — handleAlertNavigate silently dropped clicks when the
//    admin lacked the specific permission for the target view.
//    For the "alerts" view itself this means "view_alerts"
//    permission is required — a super admin without that
//    permission couldn't even open their own alerts page from
//    the bell dropdown. Fixed: null permission (open to all
//    admins) now correctly passes hasPermission, and super admins
//    skip the permission check entirely.
//
// 3. CLEANUP — handleNavChange no longer needs the require() and
//    is simplified to just navigate(NAV_TO_PATH[id] || `/${id}`).
// ─────────────────────────────────────────────────────────────
import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth }        from "../../context/useAuth";
import { useAdminAlerts } from "../../hooks/useAdminAlerts";
import Sidebar from "./components/Sidebar";
import Header  from "./components/Header";
import {
  VIEW_PERMISSIONS,
  NAV_TO_PATH,       // FIXED — was require()'d inside a function body
  PATH_TO_NAV,
  hasPermission,
} from "./page";

// ── Alerts context ────────────────────────────────────────────
export const AdminAlertsContext = createContext(null);
export function useAlertsContext() {
  return useContext(AdminAlertsContext);
}

export default function AdminLayout() {
  const { user, token }  = useAuth();
  const location         = useLocation();
  const navigate         = useNavigate();
  const adminPermissions = user?.adminPermissions || [];
  const isSuperAdmin     = user?.adminRole === "super_admin";

  // Derive active nav from URL path segment
  const currentPath = location.pathname.replace(/^\//, "").split("/")[0] || "dashboard";
  const activeNav   = PATH_TO_NAV[currentPath] || currentPath;

  // ADDED — this state never existed. Header's hamburger button was
  // calling an onMenuToggle prop that AdminLayout never passed, and
  // Sidebar's mobileOpen/onMobileClose props were never passed either
  // — so the button was wired to nothing and clicking it did nothing.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close the drawer automatically whenever the route changes
  // (e.g. after tapping a nav item that navigates via a different path)
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Live alerts
  const alertsData = useAdminAlerts({ token, enabled: !!token });
  const { alerts, unreadCount, criticalCount, connected, loading: alertsLoading, dismissLocal } = alertsData;

  // ── Navigate to a view by nav ID ──────────────────────────
  // FIXED — no longer uses require(). NAV_TO_PATH is imported above.
  const handleNavChange = (id) => {
    const perm = VIEW_PERMISSIONS[id];
    // Super admins bypass permission gates; null perm = open to all admins
    if (!isSuperAdmin && !hasPermission(adminPermissions, perm)) {
      navigate("/unauthorized");
      return;
    }
    navigate(NAV_TO_PATH[id] || `/${id}`);
  };

  // ── Navigate from an alert link ───────────────────────────
  // FIXED — was calling handleNavChange which internally did the
  // require(). Now calls navigate() directly after the permission
  // check, so the bug doesn't propagate here.
  // Also: super admins can always navigate to any view from an alert.
  const handleAlertNavigate = (view) => {
    if (!view) return;
    // "alerts" view and other views with null permission are open to
    // all authenticated admins — handle that case explicitly so the
    // check doesn't gate on undefined permission.
    const perm = VIEW_PERMISSIONS[view];
    if (!isSuperAdmin && perm !== undefined && perm !== null && !hasPermission(adminPermissions, perm)) {
      return; // silently ignore — admin can see alert but can't navigate to that section
    }
    navigate(NAV_TO_PATH[view] || `/${view}`);
  };

  // Context value
  const alertsContextValue = useMemo(() => ({
    alerts,
    unreadCount,
    alertsLoading,
    dismissLocal,
  }), [alerts, unreadCount, alertsLoading, dismissLocal]);

  return (
    <AdminAlertsContext.Provider value={alertsContextValue}>
      <div className="flex h-screen bg-[#0A0A0A] overflow-hidden">
        <Sidebar
          activeNav={activeNav}
          onNavChange={handleNavChange}
          adminPermissions={adminPermissions}
          alertUnread={unreadCount}
          alertCritical={criticalCount}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <Header
            activeNav={activeNav}
            alertUnread={unreadCount}
            alertCritical={criticalCount}
            socketConnected={connected}
            alerts={alerts}
            onAlertNavigate={handleAlertNavigate}
            onAlertDismiss={dismissLocal}
            onMenuToggle={() => setMobileMenuOpen(v => !v)}
          />
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </AdminAlertsContext.Provider>
  );
}
